// filepath: apps/web/src/admin/organization-units/new.tsx
//
// ISS-517 — OrganizationUnitNewScreen (POST /api/directory/organization-units).
//
// Mirrors the ISS-518 Person create screen (lighter SPA protocol for
// Slice 1: no react-router-dom, no raw fetch(), no synthesised display
// fields — INV-2).
//
// INV-7: row_version is SERVER-ASSIGNED on create (the Go INSERT sets
// row_version = 1, `repository.go:57`); the form does NOT carry one.
// INV-8: a fresh X-Idempotency-Key per submit — the SSR replay guard
// (`kernel.IdempotencyMiddleware`) returns the original response on a
// double-submit rather than producing a duplicate row.
//
// BA ruling note 18335 (D3): `legal_entity_id` is user-selectable and
// REQUIRED. The picker is fed by `listLegalEntities()` and is
// preselected read-only when the tenant has exactly one legal entity.
// This is a business field, NOT an INV-1 scope — the list view shows
// all tenant units and the create form does not inherit a legal-entity
// filter from the URL.

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { ApiError } from "@/definitions/types";
import { navigate } from "@/features/cabinet/usePathname";
import {
  OrganizationUnitForm,
  type OrganizationUnitCandidate,
  type OrganizationUnitFormValues,
} from "./OrganizationUnitForm";
import {
  createOrganizationUnit,
  listLegalEntities,
  listOrganizationUnits,
  newIdempotencyKey,
  type CreateOrganizationUnitInput,
} from "./api";

function isPermissionDenied(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 403 || e.status === 404;
  return false;
}

export interface OrganizationUnitNewScreenProps {
  /** Test override for the submit pipeline. */
  submit?: (input: CreateOrganizationUnitInput, key: string) => Promise<void>;
  /** Test override for the legal-entity picker feed. */
  loadLegalEntities?: () => Promise<OrganizationUnitCandidate[]>;
  /** Test override for the parent picker feed. */
  loadUnits?: () => Promise<OrganizationUnitCandidate[]>;
  /** Pre-selected parent (tree page "Add child" deep link). */
  initialParentId?: string | null;
}

export function OrganizationUnitNewScreen({
  submit,
  loadLegalEntities,
  loadUnits,
  initialParentId,
}: OrganizationUnitNewScreenProps = {}): React.ReactElement {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [legalEntityError, setLegalEntityError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);

  const [legalEntities, setLegalEntities] = useState<
    OrganizationUnitCandidate[]
  >([]);
  const [parents, setParents] = useState<OrganizationUnitCandidate[]>([]);

  useEffect(() => {
    let cancelled = false;
    const leLoader = loadLegalEntities ?? (() => listLegalEntities());
    const unitLoader =
      loadUnits ?? (() => listOrganizationUnits({ include_closed: true }));
    void Promise.all([leLoader(), unitLoader()])
      .then(([les, units]) => {
        if (cancelled) return;
        setLegalEntities(les);
        setParents(units);
      })
      .catch(() => {
        if (cancelled) return;
        // A picker that failed to load is a validation-state problem,
        // not a fatal error: the submit button stays disabled until a
        // legal entity is selected, and the operator sees why.
        setLegalEntityError(
          t(
            "directory.organization_units.new.picker_error",
            "Could not load the reference data. Reload the page and try again.",
          ),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [loadLegalEntities, loadUnits, t]);

  const onSubmit = useCallback(
    async (values: OrganizationUnitFormValues) => {
      setSubmitting(true);
      setError(null);
      setLegalEntityError(null);
      setPermissionDenied(false);
      try {
        const key = newIdempotencyKey();
        const input: CreateOrganizationUnitInput = {
          legal_entity_id: values.legal_entity_id,
          parent_organization_unit_id: values.parent_organization_unit_id,
          name: values.name,
          // Go `Validate(name, code)` rejects a present-but-blank code,
          // so an empty field is dropped (INSERT uses NULLIF($6, '')).
          code: values.code === "" ? null : values.code,
        };
        if (submit) {
          await submit(input, key);
        } else {
          await createOrganizationUnit(input, key);
        }
        navigate("/admin/organization-units");
      } catch (e) {
        if (isPermissionDenied(e)) {
          setPermissionDenied(true);
        } else if (
          e instanceof ApiError &&
          e.status === 400 &&
          e.code === "invalid_input"
        ) {
          // Go `Validate` rejected the payload (package.go:77-85).
          setError(
            t(
              "directory.organization_units.new.invalid_input",
              "The server rejected these values. Check the name and code and try again.",
            ),
          );
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [submit, t],
  );

  if (permissionDenied) {
    return (
      <div data-testid="organization-unit-new-permission-denied">
        <PermissionDenied />
      </div>
    );
  }

  return (
    <OrganizationUnitForm
      mode="create"
      heading={t(
        "directory.organization_units.new.title",
        "New organization unit",
      )}
      initial={{
        legal_entity_id: "",
        parent_organization_unit_id: initialParentId ?? null,
        name: "",
        code: "",
      }}
      legalEntityCandidates={legalEntities}
      parentCandidates={parents}
      onSubmit={onSubmit}
      onCancel={() => navigate("/admin/organization-units")}
      submitting={submitting}
      error={error}
      legalEntityError={legalEntityError}
      submitLabel={t("directory.organization_units.new.submit", "Create unit")}
      loadingLabel={t(
        "directory.organization_units.new.submitting",
        "Creating…",
      )}
    />
  );
}

export default OrganizationUnitNewScreen;
